import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getWebSocketEndpointV2 } from '../api/network_calls';
import WebSocketManagerV2, { ProgressUpdate } from '../api/websocket-manager-v2';
import { useAppSelector } from '../store/hooks';

interface WebSocketContextV2Type {
  isConnected: boolean;
  subscribeToStory: (
    storyId: string,
    callbacks: {
      onProgress?: (update: ProgressUpdate) => void;
      onInitialProgress?: (initialProgress: ProgressUpdate | null) => void;
      onComplete?: (story: any) => void;
      onError?: (error: any) => void;
    }
  ) => () => void;
  subscribeToVideo: (
    videoId: string,
    callbacks: {
      onProgress?: (update: ProgressUpdate) => void;
      onInitialProgress?: (initialProgress: ProgressUpdate | null) => void;
      onComplete?: (data: any) => void;
      onError?: (error: any) => void;
    }
  ) => () => void;
  subscribeToCharacter: (
    characterId: string,
    requestId: string,
    callbacks: {
      onProgress?: (update: ProgressUpdate) => void;
      onInitialProgress?: (initialProgress: ProgressUpdate | null) => void;
      onComplete?: (data: any) => void;
      onError?: (error: any) => void;
    }
  ) => () => void;
  subscribeToVoice: (
    voiceId: string,
    callbacks: {
      onProgress?: (update: ProgressUpdate) => void;
      onInitialProgress?: (initialProgress: ProgressUpdate | null) => void;
      onComplete?: (data: any) => void;
      onError?: (error: any) => void;
    }
  ) => () => void;
}

const WebSocketContextV2 = createContext<WebSocketContextV2Type | undefined>(undefined);

export const WebSocketProviderV2: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const userId = useAppSelector((state) => state.app.userId);
  const [isConnected, setIsConnected] = useState(false);
  const wsManager = useRef<WebSocketManagerV2 | null>(null);

  useEffect(() => {
    let cancelled = false;

    const initializeConnection = async () => {
      if (!userId) {
        wsManager.current?.close();
        wsManager.current = null;
        setIsConnected(false);
        return;
      }

      try {
        const session = await fetchAuthSession();
        if (!session.tokens?.idToken) {
          return;
        }

        if (cancelled) {
          return;
        }

        wsManager.current = WebSocketManagerV2.getInstance(getWebSocketEndpointV2());
        wsManager.current.onOpen(() => setIsConnected(true));
        wsManager.current.onClose(() => setIsConnected(false));
        await wsManager.current.connect(userId);
      } catch (_error) {
        setIsConnected(false);
      }
    };

    initializeConnection();

    return () => {
      cancelled = true;
      wsManager.current?.close();
      wsManager.current = null;
      setIsConnected(false);
    };
  }, [userId]);

  const subscribeToStory = useCallback<WebSocketContextV2Type['subscribeToStory']>(
    (storyId, callbacks) => {
      if (!wsManager.current) {
        return () => {};
      }

      return wsManager.current.subscribeToStory(storyId, callbacks);
    },
    []
  );

  const subscribeToVideo = useCallback<WebSocketContextV2Type['subscribeToVideo']>(
    (videoId, callbacks) => {
      if (!wsManager.current) {
        return () => {};
      }

      return wsManager.current.subscribeToVideo(videoId, callbacks);
    },
    []
  );

  const subscribeToCharacter = useCallback<WebSocketContextV2Type['subscribeToCharacter']>(
    (characterId, requestId, callbacks) => {
      if (!wsManager.current) {
        return () => {};
      }

      return wsManager.current.subscribeToCharacter(characterId, requestId, callbacks);
    },
    []
  );

  const subscribeToVoice = useCallback<WebSocketContextV2Type['subscribeToVoice']>(
    (voiceId, callbacks) => {
      if (!wsManager.current) {
        return () => {};
      }

      return wsManager.current.subscribeToVoice(voiceId, callbacks);
    },
    []
  );

  return (
    <WebSocketContextV2.Provider value={{ isConnected, subscribeToStory, subscribeToVideo, subscribeToCharacter, subscribeToVoice }}>
      {children}
    </WebSocketContextV2.Provider>
  );
};

export const useWebSocketV2 = (): WebSocketContextV2Type => {
  const context = useContext(WebSocketContextV2);
  if (!context) {
    throw new Error('useWebSocketV2 must be used within a WebSocketProviderV2');
  }
  return context;
};
